import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authenticatedFetch } from '../../api/ApiInterceptor';

const PlayerWallet = ({ transactions = [] }) => {
  const [walletData, setWalletData] = useState({
    balance: 0,
    withdrawable: 0,
    pending: 0,
    currency: "INR",
    coinBalance: 0
  });
  
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  
  // Add state for UPI ID management
  const [upiId, setUpiId] = useState('');
  const [savedUpiId, setSavedUpiId] = useState('');
  const [isSubmittingUpi, setIsSubmittingUpi] = useState(false);
  const [upiError, setUpiError] = useState('');
  const [upiSuccess, setUpiSuccess] = useState(false);
  const [isEditingUpi, setIsEditingUpi] = useState(false);
  const [isLoadingUpi, setIsLoadingUpi] = useState(true);
  
  // Add state for withdrawal success
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  
  useEffect(() => {
    // Get player data from localStorage
    const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
    
    // Updated to use the correct field names from API response
    setWalletData({
      balance: playerData.total_money_balance || 0,
      withdrawable: playerData.total_money_balance_withdrawable || 0,
      pending: playerData.total_money_balance_not_withdrawable || 0,
      currency: "INR",
      coinBalance: playerData.coin_balance || 0
    });
    
    // Also check if there's a saved UPI ID
    if (playerData.upi_id) {
      setSavedUpiId(playerData.upi_id);
    }
    
    // Fetch UPI ID from the backend
    fetchUpiId();
    
    // Fetch latest wallet data from the backend
    fetchWalletData();
  }, []);
  
  // Function to fetch the latest wallet data
  const fetchWalletData = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
      const playerId = userInfo?.userId || userInfo?.id || userInfo?.user_id;
      
      if (!playerId) {
        // console.error("Player ID not found");
        return;
      }
      
      const response = await authenticatedFetch(`/playerapi/players/${playerId}/`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch wallet data: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Update wallet data with values from the API
      setWalletData({
        balance: data.total_money_balance || 0,
        withdrawable: data.total_money_balance_withdrawable || 0,
        pending: data.total_money_balance_not_withdrawable || 0,
        currency: "INR",
        coinBalance: data.coin_balance || 0
      });
      
      // Update localStorage with the latest data
      localStorage.setItem("player_data", JSON.stringify(data));
      
      // If there's a UPI ID in the response, update it
      if (data.upi_id) {
        setSavedUpiId(data.upi_id);
      }
    } catch (error) {
      // console.error("Error fetching wallet data:", error);
    }
  };
  
  // Function to fetch UPI ID from backend
  const fetchUpiId = async () => {
    try {
      setIsLoadingUpi(true);
      const response = await authenticatedFetch('/playerapi/upi/', {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.upi_id) {
          setSavedUpiId(data.upi_id);
          
          // Also update local storage
          const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
          playerData.upi_id = data.upi_id;
          localStorage.setItem("player_data", JSON.stringify(playerData));
        }
      } else {
        // console.log('No UPI ID found or failed to fetch');
      }
    } catch (error) {
      // console.error("Error fetching UPI ID:", error);
    } finally {
      setIsLoadingUpi(false);
    }
  };
  
  // Format amount for display
  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Get appropriate styling for transaction type
  const getTransactionTypeStyle = (type) => {
    switch(type) {
      case 'deposit':
        return { bg: 'bg-green-100', text: 'text-green-800', icon: '💰' };
      case 'withdrawal':
        return { bg: 'bg-red-100', text: 'text-red-800', icon: '💸' };
      case 'prize':
        return { bg: 'bg-purple-100', text: 'text-purple-800', icon: '🏆' };
      case 'entry':
        return { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🎮' };
      case 'refund':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '↩️' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', icon: '💵' };
    }
  };
  
  // Handle withdrawal request
  const handleWithdraw = () => {
    const amount = parseFloat(withdrawAmount);
    
    if (isNaN(amount) || amount <= 0) {
      setWithdrawError('Please enter a valid amount');
      return;
    }
    
    if (amount > walletData.withdrawable) {
      setWithdrawError('Amount exceeds withdrawable balance');
      return;
    }
    
    if (amount < 100) {
      setWithdrawError('Minimum withdrawal amount is ₹100');
      return;
    }
    
    // Simply show a success message - no API call
    setWithdrawSuccess(true);
    
    // You could also update the local state to reflect the withdrawal
    // This simulates what would happen if an API call succeeded
    setWalletData(prev => ({
      ...prev,
      balance: prev.balance - amount,
      withdrawable: prev.withdrawable - amount
    }));
    
    setTimeout(() => {
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setWithdrawSuccess(false);
    }, 5000);
  };

  // Handle UPI ID submission
  const handleUpiSubmit = async (e) => {
    e.preventDefault();
    setUpiError('');
    setUpiSuccess(false);
    
    // Basic UPI ID validation
    const upiRegex = /^[a-zA-Z0-9.-]{2,256}@[a-zA-Z][a-zA-Z]{2,64}$/;
    if (!upiRegex.test(upiId)) {
      setUpiError('Please enter a valid UPI ID (e.g., username@bankname)');
      return;
    }
    
    try {
      setIsSubmittingUpi(true);
      
      const response = await authenticatedFetch('/playerapi/upi/', {
        method: 'POST',
        body: JSON.stringify({ upi_id: upiId })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save UPI ID: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Update the saved UPI ID
      setSavedUpiId(upiId);
      setUpiSuccess(true);
      setIsEditingUpi(false);
      
      // Update player data in localStorage
      const playerData = JSON.parse(localStorage.getItem("player_data") || "{}");
      playerData.upi_id = upiId;
      localStorage.setItem("player_data", JSON.stringify(playerData));
      
      // Show success message temporarily
      setTimeout(() => {
        setUpiSuccess(false);
      }, 3000);
      
    } catch (error) {
      // console.error("Error saving UPI ID:", error);
      setUpiError(error.message || "Failed to save UPI ID. Please try again.");
    } finally {
      setIsSubmittingUpi(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Wallet</h2>
      
      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Balance</p>
              <p className="text-2xl font-bold text-gray-800">{formatAmount(walletData.balance)}</p>
            </div>
          </div>
          <div className="text-xs text-gray-500">Updated today</div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Available for Withdrawal</p>
              <p className="text-2xl font-bold text-green-600">{formatAmount(walletData.withdrawable)}</p>
            </div>
          </div>
          <button 
            onClick={() => {
              if (!savedUpiId && !isLoadingUpi) {
                // If no UPI ID, prompt to add one first
                setIsEditingUpi(true);
                setUpiId('');
              } else {
                setShowWithdrawModal(true);
              }
            }}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded py-2 text-sm transition shadow-sm flex items-center justify-center"
            disabled={walletData.withdrawable < 100 || isLoadingUpi}
          >
            {isLoadingUpi ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Withdraw Funds
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">Minimum withdrawal: ₹100</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Coin Balance</p>
              <p className="text-2xl font-bold text-yellow-600">{walletData.coinBalance}</p>
            </div>
          </div>
          <div className="text-xs text-gray-500">Use for tournament entries</div>
        </div>
      </div>
      
      {/* Improved UPI ID Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Payment Details
          </h3>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                UPI ID for Withdrawals
              </h4>
              {!isEditingUpi && savedUpiId && (
                <button 
                  onClick={() => {
                    setIsEditingUpi(true);
                    setUpiId(savedUpiId);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                >
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit
                </button>
              )}
            </div>
            
            {isLoadingUpi ? (
              <div className="py-8 flex justify-center">
                <div className="animate-pulse flex space-x-3">
                  <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                  <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                  <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                </div>
              </div>
            ) : !isEditingUpi && savedUpiId ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-blue-600 mr-4 shadow-sm border border-blue-100">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{savedUpiId}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Your withdrawals will be sent to this UPI ID
                    </p>
                  </div>
                </div>
                {upiSuccess && (
                  <div className="mt-3 text-sm text-green-600 bg-green-50 py-2 px-3 rounded-md border border-green-200 flex items-center">
                    <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    UPI ID updated successfully!
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleUpiSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-4">
                  <div className="mb-3">
                    <label htmlFor="upi_id" className="block text-sm font-medium text-gray-700 mb-1">
                      Enter your UPI ID
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <div className="relative flex items-stretch flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          id="upi_id"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                          placeholder="username@bankname"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                          required
                        />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      This UPI ID will be used for all your withdrawal requests
                    </p>
                  </div>
                  
                  {upiError && (
                    <div className="mb-3 text-sm text-red-600 bg-red-50 py-2 px-3 rounded-md border border-red-200 flex items-center">
                      <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {upiError}
                    </div>
                  )}
                </div>
                
                <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 border-t border-gray-200 flex justify-end space-x-2">
                  {isEditingUpi && (
                    <button
                      type="button"
                      onClick={() => setIsEditingUpi(false)}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmittingUpi}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isSubmittingUpi ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Save UPI ID
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
          
          {(!isEditingUpi && savedUpiId) && (
            <div className="mt-4 flex items-start bg-blue-50 p-3 rounded-md border border-blue-100">
              <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-blue-700">
                <p className="font-medium">Important</p>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>Ensure your UPI ID is active and linked to your bank account</li>
                  <li>Withdrawals are processed within 24-48 hours of request</li>
                  <li>Minimum withdrawal amount: ₹100</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-800">Transaction History</h3>
        </div>
        
        {transactions.length > 0 ? (
          <div className="divide-y">
            {transactions.map(transaction => {
              const style = getTransactionTypeStyle(transaction.type);
              return (
                <div key={transaction.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start">
                      <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center ${style.text} mr-3 flex-shrink-0`}>
                        <span>{style.icon}</span>
                      </div>
                      <div>
                        <p className="font-medium">{transaction.description}</p>
                        <p className="text-xs text-gray-500">{formatDate(transaction.date)}</p>
                      </div>
                    </div>
                    <div className={`font-medium ${transaction.amount.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.amount}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>No transactions found</p>
            <p className="text-sm mt-1">Your transaction history will appear here</p>
          </div>
        )}
      </div>
      
      {/* Prominent message about alternative payment methods */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md shadow-sm">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">Important Payment Information</h3>
            <div className="mt-2 text-sm text-amber-700">
              <p className="font-medium">If you don't have a UPI ID or prefer another payment method:</p>
              <p className="mt-1">Contact an admin via the tournament chat section to request alternative payment methods such as Google Play redeem codes.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Withdrawal Modal with improved UI */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold">Withdraw Funds</h3>
              <button 
                onClick={() => setShowWithdrawModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {!savedUpiId ? (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-medium text-yellow-800">UPI ID Required</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Please add your UPI ID first to receive your withdrawal. All payments are processed via UPI.
                    </p>
                    <p className="text-sm text-yellow-800 font-medium mt-2">
                      No UPI ID? Please contact an admin via tournament chat to arrange alternative payment methods.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {withdrawSuccess ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Withdrawal Request Received</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Your withdrawal request for {formatAmount(withdrawAmount)} has been received.
                      The amount will be transferred to your UPI ID: <span className="font-medium">{savedUpiId}</span> within 1 hour.
                    </p>
                    <p className="text-xs text-gray-500">
                      If you don't receive your payment, please contact an admin via tournament chat.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-4 flex items-start">
                      <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-blue-700 font-medium">Funds will be transferred to your UPI ID:</p>
                        <p className="text-sm font-mono mt-1">{savedUpiId}</p>
                        <p className="text-xs text-blue-600 mt-1">Usually processed within 1 hour</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Available Balance</label>
                      <p className="font-bold text-xl text-gray-800">{formatAmount(walletData.withdrawable)}</p>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Withdrawal Amount</label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">₹</span>
                        </div>
                        <input
                          type="number"
                          min="100"
                          max={walletData.withdrawable}
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                          placeholder="0.00"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">INR</span>
                        </div>
                      </div>
                      {withdrawError && <p className="mt-1 text-sm text-red-600">{withdrawError}</p>}
                      <p className="mt-1 text-xs text-gray-500">Minimum withdrawal: ₹100</p>
                    </div>
                    
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4 text-amber-800">
                      <p className="text-sm font-medium">No UPI ID or prefer another method?</p>
                      <p className="text-sm mt-1">
                        Contact an admin through tournament chat to request alternative payment methods such as Google Play redeem codes.
                      </p>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowWithdrawModal(false)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-gray-800 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleWithdraw}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white transition flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Withdraw
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerWallet;
